'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { trackFunnelEvent } from '~/lib/analytics/track';

export type AuthModalView =
  | 'sign-in'
  | 'sign-up'
  | 'forgot-password'
  | 'verify-otp'
  | 'verify-mfa';

interface AuthModalContextType {
  isOpen: boolean;
  view: AuthModalView;
  returnPath: string | null;
  pendingEmail: string | null;
  openAuthModal: (view?: AuthModalView, returnPath?: string) => void;
  closeAuthModal: () => void;
  setView: (view: AuthModalView) => void;
  setPendingEmail: (email: string | null) => void;
}

const AuthModalContext = createContext<AuthModalContextType | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setViewState] = useState<AuthModalView>('sign-up');
  const [returnPath, setReturnPath] = useState<string | null>(null);
  const [pendingEmail, setPendingEmailState] = useState<string | null>(null);

  const openAuthModal = useCallback(
    (initialView: AuthModalView = 'sign-up', path?: string) => {
      setViewState(initialView);
      setReturnPath(path ?? null);
      setIsOpen(true);
      trackFunnelEvent('auth_modal_opened', { view: initialView });
    },
    [],
  );

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    // Reset to default view after animation completes
    setTimeout(() => {
      setViewState('sign-up');
      setReturnPath(null);
      setPendingEmailState(null);
    }, 300);
  }, []);

  const setView = useCallback((newView: AuthModalView) => {
    setViewState(newView);
  }, []);

  const setPendingEmail = useCallback((email: string | null) => {
    setPendingEmailState(email);
  }, []);

  return (
    <AuthModalContext.Provider
      value={{
        isOpen,
        view,
        returnPath,
        pendingEmail,
        openAuthModal,
        closeAuthModal,
        setView,
        setPendingEmail,
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}
